import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { apiInvoicesIdFormatGetOptions } from "@/generated/api/@tanstack/react-query.gen";
import ContainerInset from "@/components/container-inset";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout";
import InvoiceFormSkeleton from "@/app/(authenticated)/$merchantId/invoices/add/_components/invoice-form-skeleton.tsx";
import InvoiceForm from "@/app/(authenticated)/$merchantId/invoices/add/_components/invoice-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider";
import {
  InvoiceFormType,
  cardProcessorSchema,
  termSchema,
} from "../add/_data/invoice-form-schema";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/invoices/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();
  const merchantAccountId = selectedMerchant?.merchAccountId || "unknown";

  const detailQuery = useQuery({
    ...apiInvoicesIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": merchantAccountId,
      },
      path: { id: id },
    }),
  });

  if (detailQuery.isPending || !detailQuery.data)
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Invoices",
            href: `/${selectedMerchant?.merchAccountId}/invoices`,
          },
          {
            title: "Detail",
            href: "#",
          },
        ]}
      >
        <PageLayoutRoot>
          <PageLayoutHeader
            className={"max-w-3xl"}
            title="Invoice Detail"
            description="This invoice includes several charges that remain unpaid. It reflects partial payments made, with the remaining balance still due."
          />
          <PageLayoutContent className={"max-w-3xl"}>
            <InvoiceFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );

  const cardProcessor: z.infer<typeof cardProcessorSchema> =
    detailQuery.data?.allowCardPayment == true
      ? {
          cardProcessor: detailQuery.data.cardProcessor ?? undefined,
          allowCardPayment: true,
        }
      : { allowCardPayment: false };
  const terms: z.infer<typeof termSchema> =
    detailQuery.data.terms === "Custom"
      ? {
          terms: "Custom",
          termDays: Number(detailQuery.data.termDays),
        }
      : {
          terms: detailQuery.data.terms!,
        };
  const formData: InvoiceFormType = {
    id: detailQuery.data.id,
    subTotal: detailQuery.data.subTotal!,
    total: detailQuery.data.total ?? "0",
    ...terms,
    discount: detailQuery.data.discount ?? undefined,
    customer: detailQuery.data.customer?.["@id"] ?? undefined,
    customerTaxId: detailQuery.data.customerTaxId ?? undefined,
    currencyCode: detailQuery.data.currencyCode ?? "USD",
    ...cardProcessor,
    billAddress: {
      type: "billing",
      firstName: detailQuery.data.billingAddress?.firstName ?? undefined,
      lastName: detailQuery.data.billingAddress?.lastName ?? undefined,
      company: detailQuery.data.billingAddress?.company ?? undefined,
      country: detailQuery.data.billingAddress?.countryCode ?? undefined,
      address1: detailQuery.data.billingAddress?.address1 ?? undefined,
      address2: detailQuery.data.billingAddress?.address2 ?? undefined,
      city: detailQuery.data.billingAddress?.city ?? undefined,
      state: detailQuery.data.billingAddress?.state ?? undefined,
      zip: detailQuery.data.billingAddress?.zip ?? undefined,
      phone: detailQuery.data.billingAddress?.phone ?? undefined,
      email: detailQuery.data.billingAddress?.email ?? "",
    },
    isSameAsBilling: detailQuery.data.isSameAsBilling,
    shipAddress: {
      type: "shipping",
      firstName: detailQuery.data.shippingAddress?.firstName ?? undefined,
      lastName: detailQuery.data.shippingAddress?.lastName ?? undefined,
      company: detailQuery.data.shippingAddress?.company ?? undefined,
      country: detailQuery.data.shippingAddress?.countryCode ?? undefined,
      address1: detailQuery.data.shippingAddress?.address1 ?? undefined,
      address2: detailQuery.data.shippingAddress?.address2 ?? undefined,
      city: detailQuery.data.shippingAddress?.city ?? undefined,
      state: detailQuery.data.shippingAddress?.state ?? undefined,
      zip: detailQuery.data.shippingAddress?.zip ?? undefined,
      phone: detailQuery.data.shippingAddress?.phone ?? undefined,
      email: detailQuery.data.shippingAddress?.email ?? undefined,
    },
    orderId: detailQuery.data.orderId ?? undefined,
    purchaseOrderNumber: detailQuery.data.purchaseOrderNumber ?? undefined,
    orderDescription: detailQuery.data.orderDescription ?? undefined,
    shipping: detailQuery.data.shipping ?? undefined,
    tax: detailQuery.data.tax ?? undefined,
    taxExempt: detailQuery.data?.taxExempt,
    vatTaxRate: detailQuery.data?.vatTaxRate ?? undefined,
    vatTaxAmount: detailQuery.data?.vatTaxAmount ?? undefined,
    summaryCommodityCode: detailQuery.data?.summaryCommodityCode ?? undefined,
    nationalTax: detailQuery.data?.nationalTax ?? undefined,
    duty: detailQuery.data?.duty ?? undefined,
    merchantVat: detailQuery.data?.merchantVat ?? undefined,
    customerVat: detailQuery.data?.customerVat ?? undefined,
    shipFromZip: detailQuery.data?.shipFromZip ?? undefined,
    uniqueVatInvoice: detailQuery.data.uniqueVatInvoice ?? undefined,
    originalOrderDate: detailQuery.data?.originalOrderDate ?? undefined,
    invoiceOrderItems:
      detailQuery.data?.invoiceOrderItems?.map((item) => ({
        productDetailByLocation: item.productDetailByLocation?.["@id"] ?? "",
        quantity: item.quantity ?? "",
        unit: item.unit!,
        discount: item.discount ?? undefined,
        tax: item.tax ?? undefined,
        commodityCode: item.commodityCode ?? undefined,
        // TODO: Check again
        cost: item.cost!,
        // TODO: Check again
        currency: item.currency!,
      })) ?? [],
  };

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Invoices",
          href: `/${selectedMerchant?.merchAccountId}/invoices`,
        },
        {
          title: "Detail",
          href: "#",
        },
      ]}
    >
      <PageLayoutRoot>
        <PageLayoutHeader
          className={"max-w-3xl"}
          title="Invoice Detail"
          description="This invoice includes several charges that remain unpaid. It reflects partial payments made, with the remaining balance still due."
        />
        <PageLayoutContent className={"max-w-3xl"}>
          <InvoiceForm
            key={detailQuery.dataUpdatedAt}
            initData={formData}
            onUpdateSuccess={() => {
              detailQuery.refetch();
            }}
          />
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
